import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PAGE, SettingsHeader } from './SettingsUI';
import { Plus, Pencil, Trash2, X, ChevronLeft, BookOpen, Search, Upload, Loader2, Lock } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';

const DOW_OPTIONS = [
  { value: 'weekday',        label: 'Weekday (Mon–Fri)' },
  { value: 'saturday',       label: 'Saturday' },
  { value: 'sunday',         label: 'Sunday' },
  { value: 'public_holiday', label: 'Public Holiday' },
];
const TIME_OPTIONS = [
  { value: 'daytime',   label: 'Daytime' },
  { value: 'evening',   label: 'Evening' },
  { value: 'night',     label: 'Night' },
  { value: 'overnight', label: 'Overnight' },
];
const DOW_LABELS = { weekday: 'Weekday', saturday: 'Saturday', sunday: 'Sunday', public_holiday: 'Public Holiday' };
const TIME_LABELS = { daytime: 'Daytime', evening: 'Evening', night: 'Night', overnight: 'Overnight' };

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-base font-semibold text-slate-800">{title}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
            <X size={16} />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

// ── Price Book List ────────────────────────────────────────────────────────────
function PriceBookList({ onSelect }) {
  const confirm = useConfirm();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingGuide, setLoadingGuide] = useState(false);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ name: '', externalId: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/price-books');
      setBooks(Array.isArray(data) ? data : []);
    } catch { toast.error('Failed to load price books'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadNdisGuide = async () => {
    setLoadingGuide(true);
    try {
      const { data } = await api.post('/price-books/load-ndis-guide', {});
      toast.success(`${data.message} — verify rates against the current NDIS guide`);
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to load NDIS guide'); }
    finally { setLoadingGuide(false); }
  };

  const create = async () => {
    if (!form.name.trim()) return toast.error('Name required');
    setSaving(true);
    try {
      await api.post('/price-books', form);
      toast.success('Price book created');
      setModal(false);
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const del = async (pb) => {
    if (!await confirm(`Delete "${pb.name}"? This will remove all items.`, { title: 'Delete Price Book', danger: true })) return;
    try {
      await api.delete(`/price-books/${pb.id}`);
      toast.success('Deleted');
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className={PAGE}>
      <SettingsHeader
        title="Price books"
        description="NDIS support item rates, used when invoices are generated."
        actions={<>
          <button onClick={loadNdisGuide} disabled={loadingGuide}
            className="btn-secondary" title="Create a price book pre-filled with the built-in NDIS catalogue (indicative prices — verify against the current guide or import the official CSV)">
            {loadingGuide ? <Loader2 size={14} className="animate-spin" /> : <BookOpen size={14} />} Load NDIS Price Guide
          </button>
          <button onClick={() => { setForm({ name: '', externalId: '' }); setModal(true); }} className="btn-primary">
            <Plus size={14} /> New Price Book
          </button>
        </>}
      />

      {books.length === 0 ? (
        <div className="card p-12 text-center">
          <BookOpen size={32} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 font-medium">No price books yet</p>
          <p className="text-sm text-slate-400 mt-1">Create a price book and add NDIS support item rates</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {books.map(pb => (
            <div key={pb.id}
              className="card p-5 flex items-center gap-4 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => onSelect(pb)}>
              <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center shrink-0">
                <BookOpen size={18} className="text-primary-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-800">{pb.name}</p>
                <p className="text-sm text-slate-400">{pb.external_id && <span className="mr-3">{pb.external_id}</span>}{pb.item_count} items</p>
              </div>
              <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                <button onClick={() => onSelect(pb)}
                  className="btn-secondary btn-sm">View / Edit</button>
                {(pb.name || '').trim().toLowerCase() === 'ndis 2025-26 (official)' ? (
                  <span title="Official NDIS price book — protected" className="p-2 text-slate-300"><Lock size={14} /></span>
                ) : (
                  <button onClick={() => del(pb)}
                    className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <Modal title="New Price Book" onClose={() => setModal(false)}>
          <Field label="Price Book Name" required>
            <input className="input-field" placeholder="e.g. NDIS Standard July 2025"
              value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          </Field>
          <Field label="External Reference">
            <input className="input-field" placeholder="e.g. ndis_2025"
              value={form.externalId} onChange={e => setForm(f => ({ ...f, externalId: e.target.value }))} />
          </Field>
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={() => setModal(false)} className="btn-secondary btn-sm">Cancel</button>
            <button onClick={create} disabled={saving} className="btn-primary btn-sm">
              {saving ? 'Creating…' : 'Create'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── NDIS CSV Import Button ─────────────────────────────────────────────────────
function NdisImportButton({ pbId, onImported }) {
  const fileRef = useRef(null);
  const [importing, setImporting] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post(`/price-books/${pbId}/import-ndis`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(data.message);
      onImported?.();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Import failed');
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <label className={`btn-secondary flex items-center gap-1.5 shrink-0 cursor-pointer ${importing ? 'opacity-50 pointer-events-none' : ''}`}
           title="Upload the official NDIA support catalog CSV to import all prices">
      {importing ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
      {importing ? 'Importing…' : 'Import NDIS CSV'}
      <input type="file" accept=".csv" className="hidden" ref={fileRef} onChange={handleFile} disabled={importing} />
    </label>
  );
}

// ── Price Book Detail (items) ──────────────────────────────────────────────────
function PriceBookDetail({ priceBookId, onBack }) {
  const confirm = useConfirm();
  const [pb, setPb] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null); // {item?}
  const [saving, setSaving] = useState(false);

  const EMPTY_FORM = { supportItemNumber: '', supportItemName: '', rate: '', dayOfWeek: 'weekday', timeOfDay: 'daytime', effectiveDate: '', kmRate: '', kmReference: '', multiplier: '1', gst: false, unit: 'hourly' };
  const [form, setForm] = useState(EMPTY_FORM);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/price-books/${priceBookId}`);
      setPb(data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [priceBookId]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY_FORM); setModal({}); };
  const openEdit = item => {
    setForm({
      supportItemNumber: item.support_item_number || '',
      supportItemName: item.support_item_name || '',
      rate: item.rate || '',
      dayOfWeek: item.day_of_week || 'weekday',
      timeOfDay: item.time_of_day || 'daytime',
      effectiveDate: item.effective_date?.split('T')[0] || '',
      kmRate: item.km_rate || '',
      kmReference: item.km_reference || '',
      multiplier: item.multiplier || '1',
      gst: item.gst || false,
      unit: item.unit || 'hourly',
    });
    setModal({ item });
  };

  const save = async () => {
    if (!form.supportItemName.trim()) return toast.error('Support item name required');
    if (!form.rate || isNaN(parseFloat(form.rate))) return toast.error('Valid rate required');
    setSaving(true);
    try {
      let resp;
      if (modal.item) {
        resp = await api.put(`/price-books/items/${modal.item.id}`, form);
        toast.success('Updated');
      } else {
        resp = await api.post(`/price-books/${priceBookId}/items`, form);
        toast.success('Item added');
      }
      // Soft NDIS-cap warning (rate exceeds indicative price-guide limit)
      if (resp?.data?.warning) {
        toast(resp.data.warning, { icon: '⚠️', duration: 7000, style: { background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' } });
      }
      setModal(null);
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const del = async (item) => {
    if (!await confirm(`Delete "${item.support_item_name}"?`, { title: 'Delete Item', danger: true })) return;
    try {
      await api.delete(`/price-books/${priceBookId}/items/${item.id}`);
      toast.success('Deleted');
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!pb) return null;

  const items = (pb.items || []).filter(i =>
    !search.trim() ||
    i.support_item_name?.toLowerCase().includes(search.toLowerCase()) ||
    i.support_item_number?.toLowerCase().includes(search.toLowerCase())
  );

  const DOW_COLOR = {
    weekday:        'bg-blue-50 text-blue-700',
    saturday:       'bg-violet-50 text-violet-700',
    sunday:         'bg-amber-50 text-amber-700',
    public_holiday: 'bg-red-50 text-red-700',
  };

  return (
    <div className={PAGE}>
      <div className="flex items-center gap-3">
        <button onClick={onBack} aria-label="Back to price books" className="btn-ghost btn-sm p-2"><ChevronLeft size={16} /></button>
        <div className="flex-1 min-w-0">
          <h1 className="truncate text-[22px] font-semibold leading-tight tracking-[-0.02em] text-slate-900">{pb.name}</h1>
          <p className="text-xs text-slate-400 mt-0.5">
            {pb.external_id && <span className="mr-3 font-mono">{pb.external_id}</span>}
            {pb.items?.length || 0} items — rates used dynamically in invoice generation
          </p>
        </div>
        <NdisImportButton pbId={pb.id} onImported={load} />
        <button onClick={openNew} className="btn-primary flex items-center gap-1.5 shrink-0">
          <Plus size={14} /> Add Rate
        </button>
      </div>

      <div className="relative max-w-xs">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input className="input-field pl-8 text-sm" placeholder="Search items…"
          value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      <div className="card overflow-hidden">
        {items.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-10">
            {pb.items?.length === 0 ? 'No items yet — click "Add Rate" to add NDIS rates' : 'No items match your search'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-500">Day of Week</th>
                  <th className="text-left px-3 py-3 text-xs font-medium text-slate-500">Time</th>
                  <th className="text-right px-3 py-3 text-xs font-medium text-slate-500">Price</th>
                  <th className="text-left px-3 py-3 text-xs font-medium text-slate-500">Effective Date</th>
                  <th className="text-left px-3 py-3 text-xs font-medium text-slate-500">Reference No.</th>
                  <th className="text-right px-3 py-3 text-xs font-medium text-slate-500">Price Per Km</th>
                  <th className="text-left px-3 py-3 text-xs font-medium text-slate-500">Reference No. Km.</th>
                  <th className="text-right px-3 py-3 text-xs font-medium text-slate-500">Multiplier</th>
                  <th className="px-3 py-3" />
                </tr>
              </thead>
              <tbody>
                {items.map((item, i) => (
                  <tr key={item.id} className={`border-b border-slate-50 last:border-0 ${i % 2 === 0 ? '' : 'bg-slate-50/40'}`}>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${DOW_COLOR[item.day_of_week] || 'bg-slate-100 text-slate-600'}`}>
                        {DOW_LABELS[item.day_of_week] || item.day_of_week}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-slate-600">{TIME_LABELS[item.time_of_day] || item.time_of_day || 'Daytime'}</td>
                    <td className="px-3 py-3 text-right font-semibold text-slate-800">${parseFloat(item.rate).toFixed(2)}</td>
                    <td className="px-3 py-3 text-slate-500 text-xs">{item.effective_date?.split('T')[0] || '—'}</td>
                    <td className="px-3 py-3 font-mono text-xs text-slate-600">{item.support_item_number || '—'}</td>
                    <td className="px-3 py-3 text-right text-slate-600">{parseFloat(item.km_rate) > 0 ? `$${parseFloat(item.km_rate).toFixed(2)}` : '—'}</td>
                    <td className="px-3 py-3 font-mono text-xs text-slate-600">{item.km_reference || '—'}</td>
                    <td className="px-3 py-3 text-right text-slate-600">{item.multiplier || 1}</td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        <button onClick={() => openEdit(item)}
                          className="p-1.5 text-slate-400 hover:text-primary-500 hover:bg-primary-50 rounded transition-colors">
                          <Pencil size={13} />
                        </button>
                        <button onClick={() => del(item)}
                          className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit modal */}
      {modal && (
        <Modal title={modal.item ? 'Edit Rate' : 'Add Rate'} onClose={() => setModal(null)}>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Day of Week" required>
              <select className="input-field" value={form.dayOfWeek} onChange={e => setForm(f => ({ ...f, dayOfWeek: e.target.value }))}>
                {DOW_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
            <Field label="Time of Day">
              <select className="input-field" value={form.timeOfDay} onChange={e => setForm(f => ({ ...f, timeOfDay: e.target.value }))}>
                {TIME_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Support Item Name" required>
            <input className="input-field" placeholder="e.g. Assistance With Self Care Activities - Standard - Weekday"
              value={form.supportItemName} onChange={e => setForm(f => ({ ...f, supportItemName: e.target.value }))} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Reference No. (Support Item)" required>
              <input className="input-field font-mono text-sm" placeholder="01_011_0107_1_1"
                value={form.supportItemNumber} onChange={e => setForm(f => ({ ...f, supportItemNumber: e.target.value }))} />
            </Field>
            <Field label="Rate ($/hr)" required>
              <input type="number" step="0.01" min="0" className="input-field"
                placeholder="70.23" value={form.rate} onChange={e => setForm(f => ({ ...f, rate: e.target.value }))} />
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Effective Date">
              <input type="date" className="input-field" value={form.effectiveDate}
                onChange={e => setForm(f => ({ ...f, effectiveDate: e.target.value }))} />
            </Field>
            <Field label="Price Per Km ($)">
              <input type="number" step="0.0001" min="0" className="input-field" placeholder="1.00"
                value={form.kmRate} onChange={e => setForm(f => ({ ...f, kmRate: e.target.value }))} />
            </Field>
            <Field label="Multiplier">
              <input type="number" step="0.01" min="0" className="input-field" placeholder="1"
                value={form.multiplier} onChange={e => setForm(f => ({ ...f, multiplier: e.target.value }))} />
            </Field>
          </div>
          <Field label="Km Reference No.">
            <input className="input-field font-mono text-sm" placeholder="01_799_0107_1_1"
              value={form.kmReference} onChange={e => setForm(f => ({ ...f, kmReference: e.target.value }))} />
          </Field>
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={() => setModal(null)} className="btn-secondary btn-sm">Cancel</button>
            <button onClick={save} disabled={saving} className="btn-primary btn-sm">
              {saving ? 'Saving…' : modal.item ? 'Save Changes' : 'Add Rate'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function PriceBooks() {
  const [selectedId, setSelectedId] = useState(null);

  if (selectedId) {
    return <PriceBookDetail priceBookId={selectedId} onBack={() => setSelectedId(null)} />;
  }
  return <PriceBookList onSelect={pb => setSelectedId(pb.id)} />;
}
